import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DayInLifeSection = () => {
  const [activeTime, setActiveTime] = useState(0);

  const dailySchedule = [
    {
      id: 0,
      time: "6:00 AM",
      title: "Morning Reflection",
      activity: "Planning & Learning",
      description: "Start the day with goal setting and catching up on tech news. Review community messages and plan mentorship sessions.",
      details: `Every morning begins with a cup of coffee and 30 minutes of reading. Whether it's the latest React updates, industry trends, or community feedback, this quiet time sets the tone for a productive day. I review my mentorship calendar and prepare materials for upcoming sessions.`,
      icon: "Sunrise",
      color: "from-accent to-primary",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      tools: ["Notion", "Feedly", "WhatsApp", "Calendar"]
    },
    {
      id: 1,
      time: "9:00 AM",
      title: "Deep Work",
      activity: "Coding & Development",
      description: "Focus time for complex problem-solving, feature development, and code reviews. This is when the magic happens.",
      details: `The most productive hours are dedicated to deep, focused work. Whether it's architecting a new feature, debugging complex issues, or reviewing pull requests, this time is sacred. I use the Pomodoro technique to maintain focus and ensure quality output.`,
      icon: "Code",
      color: "from-primary to-secondary",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop",
      tools: ["VS Code", "GitHub", "Figma", "Postman"]
    },
    {
      id: 2,
      time: "1:00 PM",
      title: "Community Engagement",
      activity: "Mentorship & Teaching",
      description: "One-on-one mentorship sessions, CodersHub activities, and answering community questions. This is where individual growth meets collective impact.",
      details: `Afternoons are reserved for the community. Whether it's a scheduled mentorship session, answering questions in the CodersHub group, or conducting a workshop, this time is about giving back and fostering growth in others.`,
      icon: "Users",
      color: "from-secondary to-success",
      image: "/me.png",
      alt: "Professional headshot of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      tools: ["Zoom", "Slack", "Miro", "Google Meet"]
    },
    {
      id: 3,
      time: "4:00 PM",
      title: "Collaboration",
      activity: "Team Meetings & Planning",
      description: "Sync with team members, stakeholder meetings, and strategic planning. Ensuring everyone is aligned and moving forward together.",
      details: `Late afternoon is collaboration time. Team standups, client meetings, and strategic planning sessions happen here. It's about ensuring everyone is aligned, removing blockers, and maintaining momentum on projects.`,
      icon: "MessageSquare",
      color: "from-success to-accent",
      image: "/images/about/me4.jpg",
      alt: "Professional headshot of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      tools: ["Teams", "Jira", "Confluence", "Calendly"]
    },
    {
      id: 4,
      time: "7:00 PM",
      title: "Content Creation",
      activity: "Writing & Sharing",
      description: "Blog posts, social media content, and documentation. Sharing knowledge and experiences with the broader community.",
      details: `Evenings are for content creation and knowledge sharing. Writing blog posts, creating educational content, updating documentation, or preparing for speaking engagements. It's about scaling impact beyond direct interactions.`,
      icon: "PenTool",
      color: "from-accent to-primary",
      image: "/me.png",
      alt: "Professional headshot of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      tools: ["Medium", "Canva", "OBS Studio", "Grammarly"]
    },
    {
      id: 5,
      time: "9:00 PM",
      title: "Personal Growth",
      activity: "Learning & Reflection",
      description: "Personal projects, skill development, and reflection on the day's achievements and lessons learned.",details: `The day ends with personal growth activities. Working on side projects, learning new technologies, or simply reflecting on the day's experiences. This time is about continuous improvement and staying curious.`,icon: "BookOpen",color: "from-primary to-secondary",image: "/images/about/me3.jpg",
      tools: ["Udemy", "YouTube", "GitHub", "Journal"]
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-1 bg-success rounded-full"></div>
            <span className="text-success font-medium tracking-wider uppercase text-sm">
              Daily Life
            </span>
            <div className="w-12 h-1 bg-success rounded-full"></div>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-secondary mb-6">
            A Day in
            <span className="text-gradient-gold"> My Life</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Where coding meets community building, and individual growth intersects with collective impact.
          </p>
        </div>

        {/* Timeline Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {dailySchedule?.map((schedule, index) => (
            <button
              key={schedule?.id}
              onClick={() => setActiveTime(index)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                activeTime === index
                  ? 'bg-success text-white shadow-lg'
                  : 'bg-muted text-muted-foreground hover:bg-success/10 hover:text-success'
              }`}
            >
              {schedule?.time}
            </button>
          ))}
        </div>

        {/* Active Schedule Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${dailySchedule?.[activeTime]?.color} flex items-center justify-center shadow-lg`}>
                  <Icon name={dailySchedule?.[activeTime]?.icon} size={28} className="text-white" />
                </div>
                <div>
                  <div className="text-sm text-success font-medium mb-1">
                    {dailySchedule?.[activeTime]?.time}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-secondary">
                    {dailySchedule?.[activeTime]?.title}
                  </h3>
                  <p className="text-lg text-muted-foreground">
                    {dailySchedule?.[activeTime]?.activity}
                  </p>
                </div>
              </div>

              <p className="text-lg text-foreground leading-relaxed">
                {dailySchedule?.[activeTime]?.description}
              </p>

              <p className="text-muted-foreground leading-relaxed">
                {dailySchedule?.[activeTime]?.details}
              </p>
            </div>

            {/* Tools Used */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-secondary flex items-center space-x-2">
                <Icon name="Tool" size={20} />
                <span>Tools & Platforms</span>
              </h4>
              <div className="flex flex-wrap gap-3">
                {dailySchedule?.[activeTime]?.tools?.map((tool, index) => (
                  <div key={index} className="bg-muted rounded-lg px-3 py-2 text-sm font-medium text-foreground">
                    {tool}
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center space-x-4 pt-4">
              <button
                onClick={() => setActiveTime(Math.max(0, activeTime - 1))}
                disabled={activeTime === 0}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronLeft" size={16} />
                <span>Previous</span>
              </button>
              <button
                onClick={() => setActiveTime(Math.min(dailySchedule?.length - 1, activeTime + 1))}
                disabled={activeTime === dailySchedule?.length - 1}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-success text-white hover:bg-success/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
          </div>

          {/* Image */}
          <div className="relative animate-slide-up">
            <div className="relative rounded-2xl overflow-hidden shadow-elevated">
              <Image
                src={dailySchedule?.[activeTime]?.image}
                alt={`${dailySchedule?.[activeTime]?.title} activity`}
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 via-transparent to-transparent"></div>
              
              {/* Overlay Content */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${dailySchedule?.[activeTime]?.color} flex items-center justify-center`}>
                        <Icon name={dailySchedule?.[activeTime]?.icon} size={16} className="text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-secondary">
                          {dailySchedule?.[activeTime]?.title}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {dailySchedule?.[activeTime]?.time}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-success font-medium">
                        {activeTime + 1} of {dailySchedule?.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
              <div className="flex space-x-2">
                {dailySchedule?.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === activeTime ? 'bg-success w-8' : 'bg-border'
                    }`}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-16 grid md:grid-cols-4 gap-6">
          <div className="text-center p-6 bg-white rounded-xl shadow-elegant">
            <Icon name="Clock" size={32} className="text-success mx-auto mb-3" />
            <div className="text-2xl font-bold text-secondary">15+</div>
            <div className="text-muted-foreground">Hours Daily</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-elegant">
            <Icon name="Users" size={32} className="text-primary mx-auto mb-3" />
            <div className="text-2xl font-bold text-secondary">10+</div>
            <div className="text-muted-foreground">People Mentored</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-elegant">
            <Icon name="Code" size={32} className="text-accent mx-auto mb-3" />
            <div className="text-2xl font-bold text-secondary">500+</div>
            <div className="text-muted-foreground">Lines of Code</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-elegant">
            <Icon name="Heart" size={32} className="text-error mx-auto mb-3" />
            <div className="text-2xl font-bold text-secondary">100%</div>
            <div className="text-muted-foreground">Passion Driven</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DayInLifeSection;